import type { EffectiveAccess } from "../services/authService";

/** DB-backed admin role codes from `effective_access.admin_roles` (verify snapshot). */
export const ADMIN_ROLE = {
  SUPER_ADMIN: "SUPER_ADMIN",
  CONTRACT_ADMIN: "CONTRACT_ADMIN",
  RFP_ADMIN: "RFP_ADMIN",
  PRIZE_RULES_ADMIN: "PRIZE_RULES_ADMIN",
} as const;

function adminRolesUpper(adminRoles: readonly string[]): string[] {
  return adminRoles.map((r) => r.trim().toUpperCase());
}

export function hasSuperAdminRole(adminRoles: readonly string[]): boolean {
  return adminRolesUpper(adminRoles).includes(ADMIN_ROLE.SUPER_ADMIN);
}

/** Library nav + `/library`: super, contract admin, RFP admin, or prize-rules admin. */
export function canSeeLibraryNavigation(adminRoles: readonly string[]): boolean {
  const u = adminRolesUpper(adminRoles);
  if (u.includes(ADMIN_ROLE.SUPER_ADMIN)) return true;
  return (
    u.includes(ADMIN_ROLE.CONTRACT_ADMIN) ||
    u.includes(ADMIN_ROLE.RFP_ADMIN) ||
    u.includes(ADMIN_ROLE.PRIZE_RULES_ADMIN)
  );
}

/** `/contracts-selection` (Library contracts tile). */
export function canAccessContractsLibraryAdmin(adminRoles: readonly string[]): boolean {
  const u = adminRolesUpper(adminRoles);
  return u.includes(ADMIN_ROLE.SUPER_ADMIN) || u.includes(ADMIN_ROLE.CONTRACT_ADMIN);
}

/** `/rfps` and admin RFP library flows. */
export function canAccessRfpLibraryAdmin(adminRoles: readonly string[]): boolean {
  const u = adminRolesUpper(adminRoles);
  return u.includes(ADMIN_ROLE.SUPER_ADMIN) || u.includes(ADMIN_ROLE.RFP_ADMIN);
}

/** `/rules-selection` (Library Game Rule tile). */
export function canAccessPrizeRulesLibraryAdmin(adminRoles: readonly string[]): boolean {
  const u = adminRolesUpper(adminRoles);
  return u.includes(ADMIN_ROLE.SUPER_ADMIN) || u.includes(ADMIN_ROLE.PRIZE_RULES_ADMIN);
}

/** Sidebar / Library: Contracts module (capability OR scoped admin OR super). */
export function canShowContractsModule(
  effectiveAccess: EffectiveAccess | null,
  adminRoles: readonly string[],
): boolean {
  if (hasSuperAdminRole(adminRoles)) return true;
  if (adminRoles.includes(ADMIN_ROLE.CONTRACT_ADMIN)) return true;
  return effectiveAccess?.can_access_contracts === true;
}

/** Sidebar / Library: RFP module. */
export function canShowRfpModule(
  effectiveAccess: EffectiveAccess | null,
  adminRoles: readonly string[],
): boolean {
  if (hasSuperAdminRole(adminRoles)) return true;
  if (adminRoles.includes(ADMIN_ROLE.RFP_ADMIN)) return true;
  return effectiveAccess?.can_access_rfp === true;
}

/** Sidebar / Library: Prize rules / Game Rule module. */
export function canShowPrizeRulesModule(
  effectiveAccess: EffectiveAccess | null,
  adminRoles: readonly string[],
): boolean {
  if (hasSuperAdminRole(adminRoles)) return true;
  if (adminRoles.includes(ADMIN_ROLE.PRIZE_RULES_ADMIN)) return true;
  return effectiveAccess?.can_access_prize_rules === true;
}
